import { useMemo, useState } from 'react';

import { apiClient } from '@infrastructure/api/client';
import { AdminListRepository } from '@infrastructure/api/AdminListRepository';
import { DataTable, type DataTableColumn } from '@app/components/data-table/DataTable';
import { formatDateTime } from '@app/lib/format';

interface AuditRow {
  id: number;
  actor_email: string;
  action: string;
  target: string;
  account_company: string;
  meta: Record<string, unknown>;
  created_at: string;
}

const repo = new AdminListRepository<AuditRow>(apiClient, 'audit-logs');

const columns: DataTableColumn<AuditRow>[] = [
  { id: 'created_at', header: 'When', accessor: (r) => formatDateTime(r.created_at) },
  { id: 'actor_email', header: 'Actor', accessor: 'actor_email' },
  { id: 'action', header: 'Action', accessor: 'action' },
  { id: 'target', header: 'Target', accessor: 'target' },
  { id: 'account_company', header: 'Account', accessor: 'account_company' },
  {
    id: 'meta',
    header: 'Detail',
    accessor: (r) => (
      <code className="font-mono text-xs text-slate-600">
        {Object.keys(r.meta || {}).length ? JSON.stringify(r.meta) : ''}
      </code>
    ),
  },
];

export function AuditPage() {
  const [search, setSearch] = useState('');
  // Admin page views are logged too and are most rows; hide them by default
  const [changesOnly, setChangesOnly] = useState(true);
  const filters = useMemo<Record<string, string>>(() => {
    const f: Record<string, string> = {};
    if (search) f.search = search;
    if (changesOnly) f.changes_only = '1';
    return f;
  }, [search, changesOnly]);

  return (
    <section>
      <div className="mb-3 flex flex-wrap items-end justify-between gap-2">
        <h2 className="text-lg font-semibold">Audit Log</h2>
        <div className="flex items-center gap-3 text-sm">
          <label className="flex items-center gap-1">
            <input type="checkbox" checked={changesOnly} onChange={(e) => setChangesOnly(e.target.checked)} />
            Changes only
          </label>
          <input
            type="search"
            placeholder="Search actor, action, target, company"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="rounded border px-2 py-1"
          />
        </div>
      </div>
      <DataTable<AuditRow>
        columns={columns}
        fetcher={(p) => repo.list({ ...p, filters })}
        pagination="offset"
        defaultPageSize={50}
        rowKey={(r) => String(r.id)}
        emptyState={<span data-testid="datatable-empty">No audit events.</span>}
      />
    </section>
  );
}
